import { CircleCheck, CircleAlert, TriangleAlert } from 'lucide-react'
import type { ReactNode } from 'react'

import { dateWithYear, formatCashAmount } from '@/lib/cash'
import type { CashStatus, CashSummary, ScenarioGap } from '@/lib/cash-summary'
import { messages } from '@/lib/messages'
import { cn } from '@/lib/utils'

const t = messages.cash.summary

const STATUS_STYLE: Record<CashStatus, { icon: typeof CircleCheck; text: string; box: string }> = {
  good: { icon: CircleCheck, text: 'text-success', box: 'border-success/30' },
  attention: { icon: TriangleAlert, text: 'text-warning', box: 'border-warning/40' },
  problem: { icon: CircleAlert, text: 'text-danger', box: 'border-danger/40' },
}

function Stat({
  label,
  value,
  hint,
  tone,
  testId,
}: {
  label: string
  value: ReactNode
  hint?: ReactNode
  tone?: 'danger' | 'warning'
  testId: string
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5" data-testid={testId}>
      <dt className="text-text-secondary text-xs">{label}</dt>
      <dd
        className={cn(
          'text-base font-semibold tabular-nums',
          tone === 'danger' && 'text-danger',
          tone === 'warning' && 'text-warning',
        )}
      >
        {value}
      </dd>
      {hint && <dd className="text-text-secondary text-xs">{hint}</dd>}
    </div>
  )
}

/**
 * Karar özeti şeridi: tek satırda durum (iyi / dikkat / sorun), altında en düşük nakit, eksi hafta
 * sayısı, eşik açığı ve varsa kötümser senaryonun baza farkı. Rakamlar `summarizeWeekly`'den gelir.
 */
export function DecisionSummary({
  summary,
  gap,
  className,
}: {
  summary: CashSummary
  gap?: ScenarioGap | null
  className?: string
}) {
  const style = STATUS_STYLE[summary.status]
  const Icon = style.icon
  const min = formatCashAmount(summary.minCash)
  const reason =
    summary.status === 'good'
      ? t.reason.good(min)
      : summary.status === 'attention'
        ? t.reason.attention(summary.belowMinWeeks, min)
        : t.reason.problem(summary.negativeWeeks)
  return (
    <section
      aria-label={t.title}
      data-testid="cash-decision-summary"
      data-status={summary.status}
      className={cn('bg-surface flex flex-col gap-3 rounded-lg border p-3', style.box, className)}
    >
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <Icon aria-hidden className={cn('size-4 shrink-0', style.text)} />
        <strong className={style.text}>{t.status[summary.status]}</strong>
        <span className="text-text-secondary">{reason}</span>
      </p>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 md:grid-cols-4">
        <Stat
          testId="cash-summary-lowest"
          label={t.lowest}
          value={summary.lowest ? formatCashAmount(summary.lowest.closing) : messages.common.empty}
          hint={summary.lowest ? t.lowestWeek(dateWithYear(summary.lowest.end)) : undefined}
          tone={summary.lowest && summary.lowest.closing < 0 ? 'danger' : undefined}
        />
        <Stat
          testId="cash-summary-negative"
          label={t.negativeWeeks}
          value={summary.negativeWeeks > 0 ? t.weeks(summary.negativeWeeks) : t.none}
          tone={summary.negativeWeeks > 0 ? 'danger' : undefined}
        />
        <Stat
          testId="cash-summary-shortfall"
          label={t.shortfall}
          value={summary.shortfall > 0 ? formatCashAmount(summary.shortfall) : t.noShortfall}
          hint={t.shortfallHint(min)}
          tone={summary.shortfall > 0 ? 'warning' : undefined}
        />
        {gap && (
          <Stat
            testId="cash-summary-scenario"
            label={t.scenario}
            value={formatCashAmount(gap.lowestDelta)}
            hint={
              gap.negativeWeeksDelta > 0
                ? `${t.scenarioHint} · ${t.scenarioNegative(gap.negativeWeeksDelta)}`
                : t.scenarioHint
            }
            tone={gap.lowestDelta < 0 ? 'danger' : undefined}
          />
        )}
      </dl>
      {summary.firstBelowMin && (
        <p className="text-text-secondary text-xs" data-testid="cash-first-below-min">
          {t.firstBelowMin(dateWithYear(summary.firstBelowMin))}
        </p>
      )}
    </section>
  )
}
